import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaSearch, FaCheckCircle, FaClock, FaExclamationCircle } from 'react-icons/fa';
import Sidebar from '../SidebarInAdminPanel/Sidebar';
import { collection, getDocs } from "firebase/firestore";
import { firestore } from '../../backend/firebaseConfig';

const ComplaintsPage = () => {
  const [complaints, setComplaints] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const navigate = useNavigate();

  useEffect(() => {
    const fetchComplaints = async () => {
      const querySnapshot = await getDocs(collection(firestore, 'Complaints'));
      const complaintsArray = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setComplaints(complaintsArray);
    };
    fetchComplaints();
  }, []);

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

  const handleComplaintClick = (complaint) => {
    navigate(`/complaint/${complaint.id}`, { state: { complaint } });
  };

  const getStatusIcon = (status) => {
    switch (status.toLowerCase()) {
      case 'pending':
        return <FaClock className="text-yellow-500" />;
      case 'resolved':
        return <FaCheckCircle className="text-green-500" />;
      default:
        return <FaExclamationCircle className="text-red-500" />;
    }
  };

  const filteredComplaints = complaints.filter(complaint => {
    const matchesSearch = complaint.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
      complaint.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
      complaint.userEmail.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === 'all' || complaint.status.toLowerCase() === filterStatus;
    return matchesSearch && matchesFilter;
  });

  return (
    <div className="flex flex-col md:flex-row bg-gradient-to-r from-[#0AD1C8] to-[#073235]">
      <Sidebar />
      <div className="flex-1 p-8">
        <h1 className="text-4xl font-bold mb-8 text-white">Complaints Dashboard</h1>

        <div className="bg-white p-6 rounded-xl shadow-md mb-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div className="bg-blue-100 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-blue-800">Total Complaints</h3>
              <p className="text-3xl font-bold text-blue-600">{complaints.length}</p>
            </div>
            <div className="bg-green-100 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-green-800">Resolved</h3>
              <p className="text-3xl font-bold text-green-600">
                {complaints.filter(c => c.status.toLowerCase() === 'resolved').length}
              </p>
            </div>
            <div className="bg-red-100 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-red-800">Pending</h3>
              <p className="text-3xl font-bold text-red-600">
                {complaints.filter(c => c.status.toLowerCase() === 'pending').length}
              </p>
            </div>
          </div>

             {/* Filters and Search */}
             <div className="flex flex-col md:flex-row justify-between items-center mb-6">
            {/* Search */}
            <div className="relative w-full md:w-1/2 mb-4 md:mb-0">
              <input
                type="text"
                placeholder="Search by subject, category, or email"
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            </div>
            {/* Status Filter */}
            <div className="flex items-center gap-4">
              <button
                onClick={() => setFilterStatus('all')}
                className={`px-4 py-2 rounded-full ${filterStatus === 'all' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
              >
                All
              </button>
              <button
                onClick={() => setFilterStatus('resolved')}
                className={`px-4 py-2 rounded-full ${filterStatus === 'resolved' ? 'bg-green-500 text-white' : 'bg-gray-200'}`}
              >
                Resolved
              </button>
              <button
                onClick={() => setFilterStatus('pending')}
                className={`px-4 py-2 rounded-full ${filterStatus === 'pending' ? 'bg-red-500 text-white' : 'bg-gray-200'}`}
              >
                Pending
              </button>
            </div>
          </div>

          <div className="overflow-x-auto bg-white rounded-xl shadow-inner">
            <div className="max-h-96 overflow-y-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="p-3 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">Email</th>
                    <th className="p-3 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">Subject</th>
                    <th className="p-3 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="p-3 text-left text-sm font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">Category</th>
                    <th className="p-3 text-left text-sm font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">Date & Time</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredComplaints.map(complaint => (
                    <tr key={complaint.id} className="hover:bg-gray-100 cursor-pointer transition-colors" onClick={() => handleComplaintClick(complaint)}>
                      <td className="p-3 text-gray-900">{complaint.userEmail}</td>
                      <td className="p-3 text-gray-900">{complaint.subject}</td>
                      <td className="p-3 text-gray-900 flex items-center">
                        {getStatusIcon(complaint.status)}
                        <span className="ml-2">{complaint.status}</span>
                      </td>
                      <td className="p-3 text-gray-900 hidden md:table-cell">{complaint.category}</td>
                      <td className="p-3 text-sm text-gray-900 hidden md:table-cell">{formatTimestamp(complaint.timestamp)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComplaintsPage;